import AddressFormatter from "@dashboard/components/AddressFormatter";
import { DashboardCard } from "@dashboard/components/Card";
import ExternalLink from "@dashboard/components/ExternalLink";
import Hr from "@dashboard/components/Hr";
import Link from "@dashboard/components/Link";
import { Text } from "@saleor/macaw-ui-next";
import React from "react";
import { useIntl } from "react-intl";

import { customerUrl } from "../../../customers/urls";
import { paymentRecoveryMessages } from "../../messages";
import { PaymentRecoveryCheckoutDetails } from "../../utils";

interface PaymentRecoveryCustomerCardProps {
  checkout: PaymentRecoveryCheckoutDetails;
}

const SectionHeader = ({ children }: { children: React.ReactNode }) => (
  <Text fontWeight="bold" display="block" marginBottom={3}>
    {children}
  </Text>
);

/**
 * A checkout-flavored fork of OrderCustomer
 * (src/orders/components/OrderCustomer) -- same Customer / Contact
 * Information / Shipping Address / Billing Address sections and
 * AddressFormatter reuse, rendered read-only: a stranded checkout has no
 * "edit and save" flow to wire an Edit button to (no order to mutate
 * against), so those buttons are simply omitted rather than added inert.
 */
export const PaymentRecoveryCustomerCard = ({ checkout }: PaymentRecoveryCustomerCardProps) => {
  const intl = useIntl();

  return (
    <DashboardCard data-test-id="payment-recovery-customer">
      <DashboardCard.Header>
        <DashboardCard.Title>
          {intl.formatMessage(paymentRecoveryMessages.customerSection)}
        </DashboardCard.Title>
      </DashboardCard.Header>
      <DashboardCard.Content>
        {checkout.user ? (
          <>
            <Text fontWeight="bold" display="block" marginBottom={1}>
              {checkout.user.email}
            </Text>
            <Link underline={false} href={customerUrl(checkout.user.id)}>
              {intl.formatMessage(paymentRecoveryMessages.viewProfile)}
            </Link>
          </>
        ) : (
          <Text>{checkout.email ?? intl.formatMessage(paymentRecoveryMessages.anonymousUser)}</Text>
        )}
      </DashboardCard.Content>

      <Hr />
      <DashboardCard.Content>
        <SectionHeader>
          {intl.formatMessage(paymentRecoveryMessages.contactInformationSection)}
        </SectionHeader>
        {checkout.email ? (
          <ExternalLink href={`mailto:${checkout.email}`}>{checkout.email}</ExternalLink>
        ) : (
          <Text>{intl.formatMessage(paymentRecoveryMessages.notSet)}</Text>
        )}
      </DashboardCard.Content>

      <Hr />
      <DashboardCard.Content data-test-id="payment-recovery-shipping-address">
        <SectionHeader>
          {intl.formatMessage(paymentRecoveryMessages.shippingAddressSection)}
        </SectionHeader>
        {checkout.shippingAddress ? (
          <AddressFormatter
            address={{ ...checkout.shippingAddress, phone: checkout.shippingAddress.phone ?? "" }}
          />
        ) : (
          <Text>{intl.formatMessage(paymentRecoveryMessages.notSet)}</Text>
        )}
      </DashboardCard.Content>

      <Hr />
      <DashboardCard.Content data-test-id="payment-recovery-billing-address">
        <SectionHeader>
          {intl.formatMessage(paymentRecoveryMessages.billingAddressSection)}
        </SectionHeader>
        {checkout.billingAddress ? (
          checkout.shippingAddress?.id === checkout.billingAddress.id ? (
            <Text>{intl.formatMessage(paymentRecoveryMessages.sameAsShipping)}</Text>
          ) : (
            <AddressFormatter
              address={{ ...checkout.billingAddress, phone: checkout.billingAddress.phone ?? "" }}
            />
          )
        ) : (
          <Text>{intl.formatMessage(paymentRecoveryMessages.notSet)}</Text>
        )}
      </DashboardCard.Content>
    </DashboardCard>
  );
};

PaymentRecoveryCustomerCard.displayName = "PaymentRecoveryCustomerCard";
export default PaymentRecoveryCustomerCard;
